import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { Video, Building2, Loader2, Send, ExternalLink, Mail, Phone, CheckCircle2, Euro, Gift, Clock } from "lucide-react";

const STATUSES = ["In attesa", "Confermato", "Completato", "Annullato"];
const statusStyle = (s) => ({
  "In attesa": "bg-amber-400/15 text-amber-800 border-amber-500/40",
  "Confermato": "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  "Completato": "bg-blue-500/15 text-blue-700 border-blue-500/30",
  "Annullato": "bg-red-500/15 text-red-700 border-red-500/30",
}[s] || "bg-stone-200 text-stone-600");

export default function AppointmentsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [confirmFor, setConfirmFor] = useState(null);
  const [paymentType, setPaymentType] = useState("free");
  const [price, setPrice] = useState("");
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/appointments"); setItems(r.data); }
    catch (e) { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { const res = await api.patch(`/appointments/${id}`, { status }); setItems((i) => i.map((x) => (x.id === id ? res.data : x))); toast.success("Stato aggiornato"); }
    catch (e) { toast.error("Errore"); }
  };

  const openConfirm = (appt) => {
    setConfirmFor(appt);
    setPaymentType("free");
    setPrice("");
  };

  const submitConfirm = async () => {
    if (paymentType === "paid" && !price.trim()) { toast.error("Inserisci l'onorario"); return; }
    setConfirming(true);
    try {
      const res = await api.post(`/appointments/${confirmFor.id}/confirm`, {
        payment_type: paymentType,
        price: paymentType === "paid" ? price.trim() : null,
      });
      setItems((i) => i.map((x) => (x.id === confirmFor.id ? res.data.appointment : x)));
      toast.success(res.data.email_sent ? "Appuntamento confermato ed email inviata al cliente" : "Appuntamento confermato (email non inviata)");
      setConfirmFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella conferma");
    } finally { setConfirming(false); }
  };

  const resend = async (id) => {
    try { const r = await api.post(`/appointments/${id}/resend`); r.data.success ? toast.success("Email inviata") : toast.error("Invio non riuscito"); }
    catch (e) { toast.error("Errore"); }
  };

  const filtered = items
    .filter((a) => filter === "all" || (filter === "online" ? a.mode === "online" : filter === "in_person" ? a.mode === "in_person" : a.status === filter))
    .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));

  const pendingCount = items.filter((a) => a.status === "In attesa").length;

  const paymentBadge = (a) => {
    if (a.payment_type === "free") return <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><Gift className="w-3.5 h-3.5" /> Gratuita</span>;
    if (a.payment_type === "paid") return <span className="inline-flex items-center gap-1 text-xs text-amber-800"><Euro className="w-3.5 h-3.5" /> A pagamento{a.price ? ` · € ${a.price}` : ""}</span>;
    return null;
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <p className="text-sm text-stone-500">
          {items.length} appuntamenti · {pendingCount > 0 ? <span className="text-amber-800 font-medium">{pendingCount} in attesa di conferma</span> : "nessuno in attesa"}
        </p>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="appointments-filter" className="bg-[#FFFFFF] border-stone-300 text-stone-900 w-full sm:w-52"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#FFFFFF] border-stone-300 text-stone-900">
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="In attesa">In attesa</SelectItem>
            <SelectItem value="online">Solo Online</SelectItem>
            <SelectItem value="in_person">Solo In Studio</SelectItem>
            <SelectItem value="Confermato">Confermati</SelectItem>
            <SelectItem value="Completato">Completati</SelectItem>
            <SelectItem value="Annullato">Annullati</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-amber-600 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="card-luxe rounded-xl p-12 text-center text-stone-500">Nessun appuntamento.</div>
      ) : (
        <div className="space-y-3" data-testid="appointments-list">
          {filtered.map((a) => (
            <div key={a.id} className={`card-luxe rounded-xl p-5 ${a.status === "In attesa" ? "ring-1 ring-amber-500/40" : ""}`} data-testid={`appointment-item-${a.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 border ${a.mode === "online" ? "bg-blue-500/10 border-blue-500/30" : "bg-amber-500/10 border-amber-500/30"}`}>
                    {a.mode === "online" ? <Video className="w-5 h-5 text-blue-700" /> : <Building2 className="w-5 h-5 text-amber-700" />}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-stone-900 font-medium">{a.client_name}</div>
                      {paymentBadge(a)}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-stone-500 mt-0.5">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {a.client_email}</span>
                      {a.client_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {a.client_phone}</span>}
                    </div>
                    <div className="text-xs text-stone-500 mt-1">{a.area} · {a.lawyer}</div>
                  </div>
                </div>
                <div className="flex flex-col items-start lg:items-end gap-1 shrink-0">
                  <div className="font-mono-legal text-amber-700 text-sm">{a.date} · {a.time}</div>
                  <div className="text-xs text-stone-500">{a.mode === "online" ? "Videoconferenza" : a.sede}</div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-stone-200">
                {a.status === "In attesa" ? (
                  <>
                    <span className={`inline-flex items-center gap-1 px-3 h-9 rounded-md text-xs border ${statusStyle(a.status)}`}><Clock className="w-3.5 h-3.5" /> In attesa</span>
                    <Button size="sm" onClick={() => openConfirm(a)} data-testid={`appointment-confirm-${a.id}`}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white h-9">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Conferma
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setStatus(a.id, "Annullato")} data-testid={`appointment-reject-${a.id}`}
                      className="border-red-500/40 text-red-600 hover:bg-red-500/10 h-9">Rifiuta</Button>
                  </>
                ) : (
                  <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                    <SelectTrigger data-testid={`appointment-status-${a.id}`} className={`w-36 h-9 text-xs border ${statusStyle(a.status)}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#FFFFFF] border-stone-300 text-stone-900">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {a.meeting_link && a.status !== "In attesa" && (
                  <a href={a.meeting_link} target="_blank" rel="noreferrer" data-testid={`appointment-link-${a.id}`}>
                    <Button size="sm" variant="outline" className="border-blue-500/40 text-blue-700 hover:bg-blue-500/10 h-9"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Avvia video</Button>
                  </a>
                )}
                {a.status === "Confermato" && (
                  <Button size="sm" variant="outline" onClick={() => resend(a.id)} data-testid={`appointment-resend-${a.id}`} className="border-amber-500/40 text-amber-700 hover:bg-amber-500/10 h-9">
                    <Send className="w-3.5 h-3.5 mr-1" /> Reinvia email
                  </Button>
                )}
                {a.note && <span className="text-xs text-stone-500 ml-auto italic truncate max-w-[200px]">{a.note}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm dialog */}
      <Dialog open={!!confirmFor} onOpenChange={(v) => !v && setConfirmFor(null)}>
        <DialogContent className="bg-[#FFFFFF] border border-amber-500/20 text-stone-900">
          <DialogHeader><DialogTitle className="font-serif text-xl">Conferma appuntamento</DialogTitle></DialogHeader>
          {confirmFor && (
            <div className="space-y-4">
              <div className="rounded-lg bg-[#F4EFE4] border border-stone-200 p-3 text-sm text-stone-600">
                <div><strong className="text-stone-900">{confirmFor.client_name}</strong></div>
                <div>{confirmFor.date} · {confirmFor.time} — {confirmFor.mode === "online" ? "Online" : confirmFor.sede}</div>
              </div>
              <div className="space-y-2">
                <Label className="text-stone-700 text-sm">Tipo di consulenza</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => setPaymentType("free")} data-testid="confirm-type-free"
                    className={`p-4 rounded-xl border text-left transition-all ${paymentType === "free" ? "border-emerald-500 bg-emerald-500/10" : "border-stone-300 hover:border-emerald-500/50"}`}>
                    <Gift className="w-5 h-5 text-emerald-600 mb-1.5" />
                    <div className="font-semibold text-stone-900 text-sm">Gratuita</div>
                    <div className="text-xs text-stone-500">Nessun onorario</div>
                  </button>
                  <button type="button" onClick={() => setPaymentType("paid")} data-testid="confirm-type-paid"
                    className={`p-4 rounded-xl border text-left transition-all ${paymentType === "paid" ? "border-amber-500 bg-amber-500/10" : "border-stone-300 hover:border-amber-500/50"}`}>
                    <Euro className="w-5 h-5 text-amber-600 mb-1.5" />
                    <div className="font-semibold text-stone-900 text-sm">A pagamento</div>
                    <div className="text-xs text-stone-500">Con onorario</div>
                  </button>
                </div>
              </div>
              {paymentType === "paid" && (
                <div className="space-y-1.5 animate-fade-up">
                  <Label className="text-stone-700 text-sm">Onorario (€)</Label>
                  <Input value={price} onChange={(e) => setPrice(e.target.value)} data-testid="confirm-price-input"
                    className="bg-[#F4EFE4] border-stone-300 text-stone-900" placeholder="Es. 150" inputMode="decimal" />
                </div>
              )}
              <p className="text-xs text-stone-500">Alla conferma verrà inviata automaticamente l'email di conferma al cliente con il riepilogo{confirmFor.mode === "online" ? " e il link video" : ""}.</p>
              <Button onClick={submitConfirm} disabled={confirming} data-testid="confirm-submit-btn"
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
                {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : "Conferma e invia email"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
